import type { HexString } from "@/types/general.types";
import { errorLog, log } from "@/features/bridge/utils/log";
import { useConfig, usePublicClient } from "wagmi";
import { waitForTransactionReceipt } from "wagmi/actions";

/**
 * A custom hook that provides utility functions for waiting for blockchain transactions
 * to complete, with enhanced fallback mechanisms for mainnet compatibility.
 *
 * @returns {Object} An object containing the following functions:
 * - `waitForTransaction`: Waits for a transaction to complete using its hash.
 * - `waitForTransactionWithTimeout`: Waits for a transaction to complete or times out,
 *    triggering optional callbacks for success, revert, timeout, and finalization.
 * - `waitForTransactionWithPolling`: Enhanced version with polling fallback for mainnet.

 */
export const useWaitForTransaction = () => {
  const config = useConfig(); // Retrieve active Wagmi config
  const publicClient = usePublicClient();

  // Basic transaction waiting (kept for backward compatibility)
  async function waitForTransaction(hash: HexString): Promise<any | null> {
    return await waitForTransactionReceipt(config, { hash });
  }

  /**
   * Enhanced transaction waiting with polling fallback for mainnet compatibility
   */
  async function waitForTransactionWithPolling(
    hash: HexString,
    maxAttempts: number = 60,
    pollInterval: number = 2000
  ): Promise<any | null> {
    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        const receipt = await publicClient?.getTransactionReceipt({ hash });
        if (receipt) {
          log(`Transaction confirmed after ${attempts + 1} polling attempts`);
          return receipt;
        }
      } catch (error) {
        // Transaction might not be mined yet, continue polling
        if (attempts === 0) {
          log("Starting transaction polling fallback...");
        }
      }

      attempts++;
      await new Promise(resolve => setTimeout(resolve, pollInterval));
    }

    throw new Error(`Transaction polling timeout after ${maxAttempts} attempts`);
  }

  async function waitForTransactionWithTimeout({
    signingHash,
    timeoutMs = 30_000,
    onSuccess,
    onRevert,
    onTimeout,
    onFinally,
    usePollingFallback = true, // New option for mainnet compatibility
  }: {
    signingHash: HexString;
    timeoutMs?: number;
    onSuccess?: (receiptHash: HexString) => void;
    onRevert?: (error: unknown) => void;
    onTimeout?: () => void;
    onFinally?: () => void;
    usePollingFallback?: boolean;
  }): Promise<void> {
    // Prevents race conditions
    let raceCompleted = false;
    let primaryMethodFailed = false;

    const primaryWaitPromise = (async () => {
      try {
        log("Attempting primary transaction wait method...");
        const response = await waitForTransaction(signingHash);
        raceCompleted = true;
        if (response.status === "success") {
          log("Primary method successful");
          onSuccess?.(response.transactionHash);
        } else {
          log("Primary method returned non-success status");
          onRevert?.(response);
        }
      } catch (error) {
        primaryMethodFailed = true;
        errorLog("Primary wait method failed:", error);

        // If polling fallback is enabled, try it
        if (usePollingFallback && !raceCompleted) {
          try {
            log("Attempting polling fallback...");
            const response = await waitForTransactionWithPolling(signingHash);
            raceCompleted = true;
            if (response.status === "success") {
              log("Polling fallback successful");
              onSuccess?.(response.transactionHash);
            } else {
              log("Polling fallback returned non-success status");
              onRevert?.(response);
            }
          } catch (pollingError) {
            raceCompleted = true;
            errorLog("Polling fallback also failed:", pollingError);
            onRevert?.(pollingError);
          }
        } else {
          raceCompleted = true;
          onRevert?.(error);
        }
      }
    })();

    const timeoutPromise = new Promise<void>((resolve) =>
      setTimeout(() => resolve(), timeoutMs),
    ).then(() => {
      if (!raceCompleted) {
        log("Transaction wait timed out");
        onTimeout?.();
      }
    });

    await Promise.race([primaryWaitPromise, timeoutPromise]);

    onFinally?.();
  }

  return {
    waitForTransaction,
    waitForTransactionWithTimeout,
    waitForTransactionWithPolling,
  };
};
